<!--
 * @Author: wjc
 * @Date: 2026-08-13 10:00:00
 * @Description: 联系我们/合作咨询弹窗，基于 reka-ui Dialog
-->
<template>
  <DialogRoot v-model:open="open">
    <DialogTrigger as-child>
      <slot>
        <button class="contact-trigger" type="button">
          <i class="i-custom-phone" aria-hidden="true"></i>
          <span>联系我们</span>
        </button>
      </slot>
    </DialogTrigger>
    <DialogPortal>
      <Transition name="overlay">
        <DialogOverlay class="contact-overlay" />
      </Transition>
      <Transition name="content">
        <DialogContent class="contact-panel" @escape-key-down="close">
          <DialogClose class="contact-close" aria-label="关闭弹窗">
            <i class="i-ep-close" aria-hidden="true"></i>
          </DialogClose>

          <!-- 合作咨询表单模式 -->
          <template v-if="mode === 'cooperation'">
            <div class="panel-header">
              <DialogTitle class="panel-title">联系我们</DialogTitle>
              <DialogDescription class="panel-desc">
                请填写基础需求信息，我们将尽快与您联系
              </DialogDescription>
            </div>
            <div v-if="loadingMeta" class="form-loading" role="status">正在加载咨询选项...</div>
            <div v-else-if="metaError" class="form-load-error" role="alert">
              <p>{{ metaError }}</p>
              <button type="button" @click="loadFeedbackMeta">重新加载</button>
            </div>
            <form v-else-if="feedbackMeta" class="cooperation-form" @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-field">
                  <span class="form-label">咨询类型</span>
                  <div class="select-control">
                    <SelectRoot v-model="form.type">
                      <SelectTrigger class="select-trigger" aria-label="咨询类型">
                        <SelectValue class="select-value" placeholder="请选择">
                          {{ selectedOptionLabel || '请选择' }}
                        </SelectValue>
                        <span class="select-arrow" aria-hidden="true"></span>
                      </SelectTrigger>
                      <SelectPortal disabled>
                        <SelectContent
                          class="select-content"
                          :side-offset="6"
                          :body-lock="false"
                          position="popper"
                          position-strategy="absolute"
                        >
                          <SelectViewport class="select-viewport">
                            <SelectGroup>
                              <SelectItem
                                v-for="opt in selectOptions"
                                :key="opt.key"
                                :value="opt.key"
                                class="select-item"
                              >
                                <SelectItemIndicator class="select-indicator">
                                  <i class="i-ep-check" aria-hidden="true"></i>
                                </SelectItemIndicator>
                                <SelectItemText>{{ opt.label }}</SelectItemText>
                              </SelectItem>
                            </SelectGroup>
                          </SelectViewport>
                        </SelectContent>
                      </SelectPortal>
                    </SelectRoot>
                  </div>
                </div>
                <label class="form-field">
                  <span class="form-label">企业/机构名称</span>
                  <input
                    v-model="form.organization"
                    :required="isRequiredContact('feedback_organization')"
                    maxlength="80"
                    autocomplete="organization"
                    placeholder="请输入企业或机构名称"
                    :aria-invalid="Boolean(fieldError('feedback_organization'))"
                  />
                  <span v-if="fieldError('feedback_organization')" class="form-error">
                    {{ fieldError('feedback_organization') }}
                  </span>
                </label>
              </div>
              <div class="form-row">
                <label class="form-field">
                  <span class="form-label">联系人</span>
                  <input
                    v-model="form.contactName"
                    :required="isRequiredContact('feedback_contact_name')"
                    maxlength="60"
                    autocomplete="name"
                    placeholder="请输入联系人姓名"
                    :aria-invalid="Boolean(fieldError('feedback_contact_name'))"
                  />
                  <span v-if="fieldError('feedback_contact_name')" class="form-error">
                    {{ fieldError('feedback_contact_name') }}
                  </span>
                </label>
                <label class="form-field">
                  <span class="form-label">联系电话</span>
                  <input
                    v-model="form.phone"
                    :required="isRequiredContact('feedback_phone')"
                    maxlength="60"
                    autocomplete="tel"
                    inputmode="tel"
                    placeholder="请输入联系电话"
                    :aria-invalid="Boolean(fieldError('feedback_phone'))"
                  />
                  <span v-if="fieldError('feedback_phone')" class="form-error">
                    {{ fieldError('feedback_phone') }}
                  </span>
                </label>
              </div>
              <label class="form-field">
                <span class="form-label">邮箱（选填）</span>
                <input
                  v-model="form.email"
                  type="email"
                  maxlength="120"
                  autocomplete="email"
                  placeholder="请输入邮箱"
                  :aria-invalid="Boolean(fieldError('feedback_email'))"
                />
                <span v-if="fieldError('feedback_email')" class="form-error">
                  {{ fieldError('feedback_email') }}
                </span>
              </label>
              <label class="form-field">
                <span class="form-label">需求描述</span>
                <textarea
                  v-model="form.message"
                  required
                  :minlength="feedbackMeta.content_min"
                  :maxlength="feedbackMeta.content_max"
                  placeholder="请简要说明合作方向、项目阶段和时间计划"
                  :aria-invalid="Boolean(fieldError('feedback_content'))"
                ></textarea>
                <span v-if="fieldError('feedback_content')" class="form-error">
                  {{ fieldError('feedback_content') }}
                </span>
              </label>
              <div class="honeypot-field" aria-hidden="true">
                <label :for="feedbackMeta.honeypot_field">昵称确认</label>
                <input
                  :id="feedbackMeta.honeypot_field"
                  v-model="form.honeypot"
                  :name="feedbackMeta.honeypot_field"
                  tabindex="-1"
                  autocomplete="off"
                />
              </div>
              <p class="form-consent-note">
                提交即表示您同意所填信息仅用于业务咨询、需求沟通及后续联系。
              </p>
              <div class="form-actions">
                <button class="btn-submit" type="submit" :disabled="submitting">
                  {{ submitting ? '提交中…' : '提交需求' }}
                </button>
                <Transition name="fade">
                  <p v-if="status" class="form-status" :class="`is-${statusType}`" role="status">
                    {{ status }}
                  </p>
                </Transition>
              </div>
            </form>
          </template>

          <!-- 联系信息展示模式 -->
          <template v-else>
            <div class="panel-header">
              <DialogTitle class="panel-title">联系我们</DialogTitle>
              <DialogDescription class="panel-desc">
                业务咨询可先查看对应信息，正式电话与邮箱以企业最终确认为准
              </DialogDescription>
            </div>
            <dl class="info-list">
              <div class="info-row">
                <dt>企业名称</dt>
                <dd>{{ companyIdentity.name }}</dd>
              </div>
              <div class="info-row">
                <dt>地址</dt>
                <dd>{{ contactInfo.address }}</dd>
              </div>
              <div class="info-row">
                <dt>电话</dt>
                <dd>{{ contactInfo.phone }}</dd>
              </div>
              <div class="info-row">
                <dt>邮箱</dt>
                <dd>{{ contactInfo.email }}</dd>
              </div>
              <div class="info-row">
                <dt>工作时间</dt>
                <dd>待企业确认</dd>
              </div>
            </dl>
          </template>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import type {
    ApiErrorResponse,
    FeedbackMeta,
    FeedbackSubmission,
    FeedbackTypeKey,
  } from '~/types/site-api'

  import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectItemIndicator,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectViewport,
  } from 'reka-ui'

  import { getFeedbackErrorMessage } from '~/utils/feedback-error'
  import { companyIdentity, contactInfo } from '~/utils/site'

  /** 弹窗模式：cooperation 为咨询表单，info 为联系信息展示 */
  type ContactMode = 'cooperation' | 'info'

  defineOptions({
    name: 'ContactDialog',
  })

  const props = withDefaults(
    defineProps<{
      defaultType?: string
      mode?: ContactMode
      target?: 'product'
      targetId?: string
    }>(),
    {
      mode: 'cooperation',
      defaultType: '',
      target: undefined,
      targetId: '',
    }
  )

  const { getFeedbackMeta, submitFeedback } = useSiteApi()
  const open = shallowRef(false)
  const submitting = shallowRef(false)
  const loadingMeta = shallowRef(false)
  const feedbackMeta = shallowRef<FeedbackMeta>()
  const metaError = shallowRef('')
  const status = shallowRef('')
  const statusType = shallowRef<'error' | 'success'>('success')
  const fieldErrors = shallowRef<Record<string, string[]>>({})

  const form = reactive({
    type: '' as '' | FeedbackTypeKey,
    organization: '',
    contactName: '',
    phone: '',
    email: '',
    message: '',
    honeypot: '',
  })

  const hasTarget = computed(() => props.target === 'product' && Boolean(props.targetId))
  const selectOptions = computed(() => {
    const options = feedbackMeta.value?.types ?? []
    return [...options].sort((left, right) => left.sort - right.sort)
  })
  const selectedOptionLabel = computed(
    () => selectOptions.value.find((option) => option.key === form.type)?.label ?? ''
  )
  function syncDefaultType() {
    const explicit = selectOptions.value.find(
      (option) => option.key === props.defaultType || option.label === props.defaultType
    )
    const purchase = hasTarget.value
      ? selectOptions.value.find((option) => option.key === 'PURCHASE')
      : undefined
    form.type = (explicit ?? purchase ?? selectOptions.value[0])?.key ?? ''
  }

  async function loadFeedbackMeta() {
    if (loadingMeta.value) return
    loadingMeta.value = true
    metaError.value = ''
    try {
      feedbackMeta.value = await getFeedbackMeta()
      syncDefaultType()
    } catch {
      metaError.value = '咨询选项加载失败，请稍后重试。'
    } finally {
      loadingMeta.value = false
    }
  }

  function isRequiredContact(field: string) {
    return feedbackMeta.value?.required_contact.includes(field) ?? false
  }

  function fieldError(field: string) {
    return fieldErrors.value[field]?.[0] ?? ''
  }

  function resetForm() {
    form.organization = ''
    form.contactName = ''
    form.phone = ''
    form.email = ''
    form.message = ''
    form.honeypot = ''
  }

  function validateForm(meta: FeedbackMeta) {
    const errors: Record<string, string[]> = {}
    const contactValues: Record<string, string> = {
      feedback_contact_name: form.contactName,
      feedback_organization: form.organization,
      feedback_phone: form.phone,
    }

    for (const field of meta.required_contact) {
      if (!contactValues[field]?.trim()) errors[field] = ['该字段不能为空。']
    }
    const contentLength = form.message.trim().length
    if (contentLength < meta.content_min || contentLength > meta.content_max) {
      errors.feedback_content = [
        `需求描述需要填写 ${meta.content_min} 至 ${meta.content_max} 个字符。`,
      ]
    }
    fieldErrors.value = errors
    return Object.keys(errors).length === 0
  }

  function applySubmitError(error: unknown) {
    const requestError = error as {
      data?: ApiErrorResponse
      status?: number
      statusCode?: number
    }
    const response = requestError.data
    const responseStatus = requestError.statusCode ?? requestError.status
    fieldErrors.value = response?.errors ?? {}
    statusType.value = 'error'
    status.value = getFeedbackErrorMessage(response, responseStatus)
  }

  function close() {
    open.value = false
  }

  async function handleSubmit() {
    const meta = feedbackMeta.value
    if (!meta || !form.type || !validateForm(meta)) return
    const selectedType = selectOptions.value.find((option) => option.key === form.type)
    if (selectedType?.requires_target && !hasTarget.value) {
      statusType.value = 'error'
      status.value = '采购咨询需要从具体产品进入。'
      return
    }

    status.value = ''
    submitting.value = true
    const payload: FeedbackSubmission = {
      feedback_type: form.type,
      feedback_content: form.message.trim(),
      feedback_organization: form.organization.trim(),
      feedback_contact_name: form.contactName.trim(),
      feedback_phone: form.phone.trim(),
      feedback_email: form.email.trim() || null,
      feedback_page_url: window.location.href,
      [meta.honeypot_field]: form.honeypot,
    }
    if (hasTarget.value && props.targetId) {
      payload.target = 'product'
      payload.target_id = props.targetId
    }

    try {
      await submitFeedback(payload)
      statusType.value = 'success'
      status.value = '提交成功，我们会尽快与您联系。'
      fieldErrors.value = {}
      resetForm()
    } catch (error) {
      applySubmitError(error)
    } finally {
      submitting.value = false
    }
  }

  watch(open, (isOpen) => {
    if (isOpen && props.mode === 'cooperation' && !feedbackMeta.value) {
      void loadFeedbackMeta()
    }
    if (!isOpen) {
      status.value = ''
      fieldErrors.value = {}
    }
  })

  watch([() => props.defaultType, () => props.target, () => props.targetId], () => {
    if (feedbackMeta.value) syncDefaultType()
  })
</script>

<style scoped lang="scss">
  /* 响应式 */
  @media (max-width: 575.98px) {
    .contact-panel {
      width: calc(100% - 24px);
      padding: 24px 20px;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .panel-title {
      font-size: clamp(17px, 5vw, 19px);
    }

    .info-row {
      grid-template-columns: 84px 1fr;
    }
  }

  .contact-trigger {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    color: inherit;
    cursor: pointer;
    background: none;
    border: 0;
  }

  /* 遮罩 */
  .contact-overlay {
    position: fixed;
    inset: 0;
    z-index: 70;
    background: rgb(15 15 20 / 55%);
    backdrop-filter: blur(4px);
  }

  /* 面板 */
  .contact-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 71;
    width: min(560px, calc(100% - 32px));
    max-height: min(85vh, 720px);
    padding: 32px;
    overflow-y: auto;
    background: var(--surface);
    border-radius: 16px;
    box-shadow:
      0 0 0 1px rgb(0 0 0 / 3%),
      0 20px 48px rgb(0 0 0 / 16%);
    transform: translate(-50%, -50%);
  }

  /* 关闭按钮 */
  .contact-close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    color: var(--muted);
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 8px;
    transition:
      background 180ms ease,
      color 180ms ease;
  }

  .close-icon {
    position: relative;
    display: block;
    width: 16px;
    height: 16px;
  }

  .close-icon::before,
  .close-icon::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 1.5px;
    content: '';
    background: currentColor;
    border-radius: 1px;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .close-icon::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  .contact-close:hover {
    color: var(--ink);
    background: var(--surface-soft);
  }

  /* 头部 */
  .panel-header {
    padding-right: 40px;
    margin-bottom: 24px;
  }

  .panel-title {
    margin: 0 0 8px;
    font-size: 22px;
    font-weight: 650;
    line-height: 1.3;
    color: var(--ink);
  }

  .panel-desc {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--muted);
  }

  /* 联系信息列表 */
  .info-list {
    display: flex;
    flex-direction: column;
    margin: 0;
  }

  .info-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 16px;
    align-items: center;
    padding: 16px 0;
    border-top: 1px solid var(--line);
  }

  .info-row:last-child {
    border-bottom: 1px solid var(--line);
  }

  .info-row dt {
    font-size: 13px;
    color: var(--muted);
  }

  .info-row dd {
    margin: 0;
    font-size: 14px;
    font-weight: 540;
    color: var(--ink);
  }

  /* 表单 */
  .cooperation-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .form-loading,
  .form-load-error {
    padding: 18px;
    font-size: 14px;
    color: var(--muted);
    background: var(--surface-soft);
    border-radius: 8px;
  }

  .form-load-error {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: space-between;

    p {
      margin: 0;
    }

    button {
      flex-shrink: 0;
      padding: 6px 0;
      font-weight: 600;
      color: var(--brand);
      cursor: pointer;
      background: transparent;
      border: 0;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-label {
    font-size: 13px;
    font-weight: 560;
    color: var(--ink);
  }

  .form-field input,
  .form-field select,
  .form-field textarea {
    width: 100%;
    min-height: 44px;
    padding: 10px 14px;
    font-size: 14px;
    color: var(--ink);
    background: var(--surface-soft);
    border: 1px solid transparent;
    border-radius: 10px;
    transition:
      border-color 180ms ease,
      background 180ms ease;
  }

  .form-field input::placeholder,
  .form-field textarea::placeholder {
    color: var(--muted);
  }

  .form-field input:focus,
  .form-field select:focus,
  .form-field textarea:focus {
    outline: none;
    background: var(--surface);
    border-color: var(--brand);
  }

  .form-field textarea {
    min-height: 100px;
    resize: vertical;
  }

  .form-error {
    font-size: 12px;
    line-height: 1.4;
    color: #b42318;
  }

  .honeypot-field {
    position: fixed;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }

  .select-trigger {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 44px;
    padding: 10px 14px;
    font-size: 14px;
    color: var(--ink);
    cursor: pointer;
    background: var(--surface-soft);
    border: 1px solid transparent;
    border-radius: 10px;
    transition:
      border-color 180ms ease,
      background 180ms ease;
  }

  .select-control {
    width: 100%;
  }

  .select-trigger:focus,
  .select-trigger[data-state='open'] {
    outline: none;
    background: var(--surface);
    border-color: var(--brand);
  }

  .select-value {
    color: inherit !important;
  }

  .select-arrow {
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-right: 1.5px solid var(--muted);
    border-bottom: 1.5px solid var(--muted);
    transform: rotate(45deg);
    transition: transform 180ms ease;
  }

  .select-trigger[data-state='open'] .select-arrow {
    transform: rotate(225deg);
  }

  :deep(.select-content) {
    z-index: 80;
    width: var(--reka-select-trigger-width);
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgb(0 0 0 / 12%);
  }

  [data-reka-popper-content-wrapper] {
    z-index: 80 !important;
  }

  .select-viewport {
    padding: 4px;
  }

  .select-item {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 9px 12px;
    font-size: 14px;
    color: var(--ink);
    cursor: pointer;
    outline: none;
    border-radius: 6px;
  }

  .select-item[data-highlighted] {
    background: var(--surface-soft);
  }

  .select-indicator {
    display: flex;
    align-items: center;
  }

  .select-indicator i {
    width: 14px;
    height: 14px;
    color: var(--brand);
  }

  /* 信息用途提示 */
  .form-consent-note {
    margin: 0;
    font-size: 12px;
    line-height: 1.65;
    color: var(--muted);
  }

  /* 提交 */
  .form-actions {
    display: flex;
    gap: 14px;
    align-items: center;
    margin-top: 4px;
  }

  .btn-submit {
    flex-shrink: 0;
    padding: 11px 28px;
    font-size: 14px;
    font-weight: 560;
    color: #fff;
    cursor: pointer;
    background: var(--brand);
    border: 0;
    border-radius: 10px;
    transition:
      background 180ms ease,
      transform 120ms ease;
  }

  .btn-submit:hover:not(:disabled) {
    background: var(--brand-strong);
  }

  .btn-submit:active:not(:disabled) {
    transform: scale(0.97);
  }

  .btn-submit:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .form-status {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }

  .form-status.is-success {
    color: #237a45;
  }

  .form-status.is-error {
    color: #b42318;
  }

  /* 动画 */
  .overlay-enter-active,
  .overlay-leave-active {
    transition: opacity 200ms ease;
  }

  .overlay-enter-from,
  .overlay-leave-to {
    opacity: 0;
  }

  .content-enter-active {
    transition:
      opacity 220ms ease,
      transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  .content-leave-active {
    transition:
      opacity 160ms ease,
      transform 160ms ease;
  }

  .content-enter-from {
    opacity: 0;
    transform: translate(-50%, calc(-50% - 16px)) scale(0.96);
  }

  .content-leave-to {
    opacity: 0;
    transform: translate(-50%, calc(-50% - 8px)) scale(0.98);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 200ms ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
